import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Megaphone, Send, Users2 } from 'lucide-react';
import { fallbackModules } from '../../data';

const AUDIENCE_OPTIONS = [
  { value: 'all_merchants', label: 'All Merchants (Owners)' },
  { value: 'specific_module', label: 'Merchants of a Specific Module' },
  { value: 'all_consumers', label: 'All Consumers' },
  { value: 'specific_tenant_list', label: 'Specific Tenants (by ID)' },
];

const CHANNEL_OPTIONS = ['in_app', 'sms', 'whatsapp', 'email'];

export function MarketingControlView() {
  const [title, setTitle] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [audience, setAudience] = useState('all_merchants');
  const [moduleCode, setModuleCode] = useState('');
  const [tenantIdsRaw, setTenantIdsRaw] = useState('');
  const [channels, setChannels] = useState<string[]>(['in_app']);
  const [sending, setSending] = useState(false);

  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fetchHistory = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setHistoryLoading(true);
    const { data, error } = await supabase
      .from('platform_broadcasts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);
    if (!error && data) setHistory(data);
    setHistoryLoading(false);
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const toggleChannel = (ch: string) => {
    setChannels((prev) => (prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]));
  };

  const resetForm = () => {
    setTitle('');
    setMessageBody('');
    setAudience('all_merchants');
    setModuleCode('');
    setTenantIdsRaw('');
    setChannels(['in_app']);
  };

  const handleSend = async () => {
    if (!title.trim() || !messageBody.trim()) {
      toast.error('Title aur message dono zaroori hain');
      return;
    }
    if (audience === 'specific_module' && !moduleCode) {
      toast.error('Module select karein');
      return;
    }
    const targetTenantIds = tenantIdsRaw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (audience === 'specific_tenant_list' && targetTenantIds.length === 0) {
      toast.error('Kam se kam ek tenant ID daalein (comma-separated)');
      return;
    }
    if (channels.length === 0) {
      toast.error('Kam se kam ek channel select karein');
      return;
    }

    const supabase = getSupabaseClient();
    if (!supabase) return;

    setSending(true);
    const { data, error } = await supabase.rpc('admin_create_broadcast', {
      p_title: title.trim(),
      p_message_body: messageBody.trim(),
      p_audience: audience,
      p_channels: channels,
      p_target_module_code: audience === 'specific_module' ? moduleCode : null,
      p_target_tenant_ids: audience === 'specific_tenant_list' ? targetTenantIds : null,
      p_dispatch_immediately: true,
    });

    if (error) {
      toast.error(error.message || 'Broadcast bhejne mein error aayi');
    } else {
      const queued = data?.dispatch_result?.queued_count ?? 0;
      toast.success(`Bheja gaya ${queued} logon ko`);
      resetForm();
      fetchHistory();
    }
    setSending(false);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div>
        <h1 className="text-2xl font-bold dark:text-white">Marketing Control</h1>
        <p className="text-slate-500 dark:text-slate-400">Platform-wide broadcasts to merchants and consumers.</p>
      </div>

      <Card className="dark:bg-slate-900 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-white">
            <Megaphone size={20} /> New Broadcast
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium dark:text-slate-300">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Diwali Offer Live!" className="dark:bg-slate-800 dark:border-slate-700 dark:text-white" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium dark:text-slate-300">Audience</label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="dark:bg-slate-800 dark:border-slate-700 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {audience === 'specific_module' && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium dark:text-slate-300">Module</label>
              <Select value={moduleCode} onValueChange={setModuleCode}>
                <SelectTrigger className="max-w-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white">
                  <SelectValue placeholder="Select module" />
                </SelectTrigger>
                <SelectContent>
                  {fallbackModules.map((m: any) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {audience === 'specific_tenant_list' && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium dark:text-slate-300">Tenant IDs (comma-separated UUIDs)</label>
              <Input
                value={tenantIdsRaw}
                onChange={(e) => setTenantIdsRaw(e.target.value)}
                placeholder="uuid-1, uuid-2, ..."
                className="dark:bg-slate-800 dark:border-slate-700 dark:text-white"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium dark:text-slate-300">Message</label>
            <Textarea
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              rows={4}
              placeholder="Message body..."
              className="dark:bg-slate-800 dark:border-slate-700 dark:text-white"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium dark:text-slate-300">Channels</label>
            <div className="flex flex-wrap gap-3">
              {CHANNEL_OPTIONS.map((ch) => (
                <label
                  key={ch}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm capitalize transition-colors ${
                    channels.includes(ch)
                      ? 'bg-primary/10 border-primary text-primary'
                      : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={channels.includes(ch)}
                    onChange={() => toggleChannel(ch)}
                    className="accent-primary"
                  />
                  {ch}
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={handleSend} disabled={sending} className="bg-primary hover:bg-primary/90 text-white gap-2">
              <Send size={16} /> {sending ? 'Sending...' : 'Send Now'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="dark:bg-slate-900 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-white">
            <Users2 size={20} /> Broadcast History
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 font-medium">
              <tr>
                <th className="px-6 py-3">Title</th>
                <th className="px-6 py-3">Audience</th>
                <th className="px-6 py-3">Channels</th>
                <th className="px-6 py-3">Sent Count</th>
                <th className="px-6 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {historyLoading && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">
                    Loading history...
                  </td>
                </tr>
              )}
              {!historyLoading && history.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    Koi broadcast abhi tak nahi bheja gaya.
                  </td>
                </tr>
              )}
              {!historyLoading &&
                history.map((b) => (
                  <tr key={b.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-6 py-3 font-semibold dark:text-white">{b.title}</td>
                    <td className="px-6 py-3 dark:text-slate-300 capitalize">{b.audience?.replace(/_/g, ' ')}</td>
                    <td className="px-6 py-3 dark:text-slate-300">
                      {(b.channels || []).join(', ')}
                    </td>
                    <td className="px-6 py-3 dark:text-slate-300">{b.sent_count ?? 0}</td>
                    <td className="px-6 py-3 text-slate-500 dark:text-slate-400">
                      {b.created_at ? new Date(b.created_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export default MarketingControlView;
